import { createIntl, createIntlCache, } from "@formatjs/intl";
import React, { createContext, useContext, useMemo, } from "react";
import invariant from "tiny-invariant";
import { formatMessageHof } from "./formatMessage";
import { processIntlConfig } from "./processIntlConfig";
const contexts = {};
const useI18n = ({ domain }) => {
    invariant(contexts[domain], `There is no bootstrapped i18n context for: ${domain}`);
    const I18nContext = contexts[domain];
    const intl = useContext(I18nContext);
    return intl;
};
const MEMOIZED_CONFIG_FIELDS = [
    "locale",
    "messages",
    "timeZone",
    "fallbackOnEmptyString",
    "formats",
    "defaultLocale",
    "defaultFormats",
    "onError",
    "onWarn",
    "defaultRichTextElements",
];
const createI18nProvider = ({ domain }) => {
    invariant(!contexts[domain], `There is already bootstrapped a i18n context for: ${domain}`);
    const domainContext = createContext(null);
    contexts[domain] = domainContext;
    const { Provider } = domainContext;
    // eslint-disable-next-line react/prop-types
    const I18nProvider = ({ children, locale, messages, timeZone, fallbackOnEmptyString, formats, defaultLocale, defaultFormats, onError, onWarn, defaultRichTextElements, }) => {
        const cache = useMemo(() => createIntlCache(), []);
        const intl = useMemo(() => {
            const config = processIntlConfig({
                locale,
                messages,
                timeZone,
                fallbackOnEmptyString,
                formats,
                defaultLocale,
                defaultFormats,
                onError,
                onWarn,
                defaultRichTextElements,
            });
            const coreIntl = createIntl(config, cache);
            return {
                ...coreIntl,
                formatMessage: formatMessageHof(config, coreIntl.formatters),
            };
            // Dependencies must stay raw props: deriving one here would allocate per render and
            // defeat the memo. `messages` relies on TanStack's structural sharing preserving the
            // reference when refetched data is deep-equal, so it must never be mutated in place.
        }, [
            cache,
            locale,
            messages,
            timeZone,
            fallbackOnEmptyString,
            formats,
            defaultLocale,
            defaultFormats,
            onError,
            onWarn,
            defaultRichTextElements,
        ]);
        invariant(intl, "Intl is not initialized");
        return React.createElement(Provider, { value: intl }, children);
    };
    return I18nProvider;
};
export { createI18nProvider, useI18n };
